import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { AuthContext } from 'contexts/AuthContext';
import { useDevice } from 'hooks/useMediaQuery';
import {
    getFreeDigDeeperContent,
    getFreeReaderAlerts,
    readerGetMyAlerts, getReaderArticles
} from 'utils/api/alertsAPI';
import { MobileReader } from './Mobile';
import { DesktopReader } from './Desktop';
import { PublicModalSignIn } from './Desktop/AlertList/Modals/PublicModalSignIn';
import { getUserAlertsForReaderById, getAlertsNewsForReaderByAlertId } from 'utils/api/usersAPI'

export const Reader = ({ isPublic, companyReader }) => {
    const { id } = useParams();
    const { search } = useLocation();
    const navigate = useNavigate();
    const { state, handleError } = useContext(AuthContext);
    const { isDesktop } = useDevice();
    const [readerAuthChecked, setReaderAuthChecked] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [articles, setArticles] = useState([]);
    const [fetchingAlerts, setFetchingAlerts] = useState(true);
    const [fetchingAlertContent, setFetchingAlertContent] = useState(false);
    const [alertContent, setAlertContent] = useState(null);
    const [allowManageAlertsInReader, setAllowManageAlertsInReader] = useState(false);
    const [publicEmail, setPublicEmail] = useState('');
    const [signInModalOpen, setSignInModalOpen] = useState(false);
    const toggleSignInModal = () => setSignInModalOpen(!signInModalOpen);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
            setSignInModalOpen(true);
        }
    }, []);

    useEffect(() => {
        if (!companyReader) {
            setReaderAuthChecked(true);
            return;
        }

        if (state && !isEmpty(state.readerInfo)) {
            const status = localStorage.getItem('readerInfo');
            if (!status || status === 'undefined') return;
            setReaderAuthChecked(true);
        }
    }, [state]);

    useEffect(() => {
        fetchAlerts('initialLoad');
    }, [state]);

    useEffect(() => {
        if (!(state.isAuthenticated || state.isReaderAuthenticated)) {
            setAllowManageAlertsInReader(false);
            return
        }

        if (!companyReader) {
            state.profile?.organization && setAllowManageAlertsInReader(!!state.profile.organization?.accesses?.myReaderAuthAccess);
        }
    }, [state]);

    const pushEmptyAlertsToEnd = (arr) => {
        let newArr = arr;
        newArr.map((item) => {
            if (item.countNews === 0) {
                let index = newArr.indexOf(item);
                newArr.splice(index, 1);
                newArr.push(item);
            }
        });
        return newArr;
    };

    const fetchAlerts = async () => {
        const urlParams = new URLSearchParams(search);
        setFetchingAlerts(true);

        try {
            let userData;

            if (isPublic) {
                if (companyReader && !state.isReaderAuthenticated) return;

                if (companyReader) {
                    userData = await getFreeReaderAlerts(state.readerInfo.id, state.readerInfo.hash, 10);
                    setAllowManageAlertsInReader(false);
                    setPublicEmail(userData.email);
                } else {
                    const hash = urlParams.get('h');
                    if (!hash) {
                        return;
                    }
                    userData = await getFreeReaderAlerts(id, hash, 10);
                    setPublicEmail(userData.email);
                    userData = userData.alerts;
                }
                setAllowManageAlertsInReader(userData.myReaderAuth);
            } else if (state.isAuthenticated) {
                userData = await getUserAlertsForReaderById(id);
                setPublicEmail(state?.profile?.id);
            } else if (state.isReaderAuthenticated) {
                userData = await readerGetMyAlerts();
                setPublicEmail(state?.profile?.id);
            }

            const newArr = userData ? pushEmptyAlertsToEnd(userData) : [];
            setAlerts(newArr);
        } catch (err) {
            handleError(err);
        }

        setFetchingAlerts(false);
    };

    const fetchAlertContent = async (alertId, alertHash) => {
        setFetchingAlertContent(true);

        try {
            let alertContent;

            if (isPublic) {
                alertContent = await getFreeDigDeeperContent(alertId, alertHash, 10);
            } else if (state.isReaderAuthenticated) {
                alertContent = await getReaderArticles(alertId);
            } else if (state.isAuthenticated) {
                alertContent = await getAlertsNewsForReaderByAlertId(alertId);
            }

            setAlertContent(alertContent);
            setArticles(alertContent?.news);
        } catch (err) {
            handleError(err);
        }

        setFetchingAlertContent(false);
    };

    if (companyReader && !state.isReaderAuthenticated) navigate('/company/reader/signin');
    if (companyReader && !readerAuthChecked) return null;
    if (!isPublic && !(state?.isReaderAuthenticated || state?.isAuthenticated)) navigate('/signin');

    if (isDesktop) {
        return (
            <>
                <DesktopReader
                    id={id}
                    isPublic={isPublic}
                    alertsData={alerts}
                    articlesData={articles}
                    alertContent={alertContent}
                    allowManageAlertsInReader={allowManageAlertsInReader}
                    fetchingAlerts={fetchingAlerts}
                    fetchingAlertContent={fetchingAlertContent}
                    fetchAlerts={fetchAlerts}
                    fetchAlertContent={fetchAlertContent}
                    signInModalOpen={signInModalOpen}
                    setSignInModalOpen={setSignInModalOpen}
                />
                {signInModalOpen &&
                    <PublicModalSignIn isOpen={signInModalOpen} isPublic={isPublic} toggle={toggleSignInModal} publicEmail={publicEmail} setPublicEmail={setPublicEmail} />}
            </>
        );
    } else {
        return (
            <MobileReader
                id={id}
                isPublic={isPublic}
                alertsData={alerts}
                articlesData={articles}
                alertContent={alertContent}
                publicEmail={publicEmail}
                fetchingAlerts={fetchingAlerts}
                fetchingAlertContent={fetchingAlertContent}
                fetchAlerts={fetchAlerts}
                fetchAlertContent={fetchAlertContent}
                setPublicEmail={setPublicEmail}
                checkLogin={signInModalOpen}
            />
        );
    }
};
